import { useState, useCallback, useMemo } from 'react';

export interface ExploreFilters {
  sort: 'match_score' | 'distance' | 'newest' | 'rating';
  query: string;
  tags: string[];
  difficulty: string | null; // 'easy' | 'moderate' | 'hard' | null
  minDistance: number | null; // metres — trail LENGTH (min_distance/max_distance)
  maxDistance: number | null;
  minUserDistance: number | null; // metres — distance from the user
  maxUserDistance: number | null;
  minDuration: number | null; // seconds
  maxDuration: number | null;
  offLeash: boolean | null;
  wildlife: boolean | null;
  minRating: number | null;
  lowDogTraffic: boolean | null;
  lowFootTraffic: boolean | null;
  lowExposure: boolean | null;
  // Location search
  searchLatitude: number | null;
  searchLongitude: number | null;
  searchLocationName: string | null;
  // Map pan state (not an active filter)
  mapCenterLatitude: number | null;
  mapCenterLongitude: number | null;
}

export const DEFAULT_FILTERS: ExploreFilters = {
  sort: 'match_score',
  query: '',
  tags: [],
  difficulty: null,
  minDistance: null,
  maxDistance: null,
  minUserDistance: null,
  maxUserDistance: null,
  minDuration: null,
  maxDuration: null,
  offLeash: null,
  wildlife: null,
  minRating: null,
  lowDogTraffic: null,
  lowFootTraffic: null,
  lowExposure: null,
  searchLatitude: null,
  searchLongitude: null,
  searchLocationName: null,
  mapCenterLatitude: null,
  mapCenterLongitude: null,
};

// Pure mapper: filter state → query params for /trail/search and /trail/search/count.
export function filtersToParams(f: ExploreFilters): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};

  if (f.sort) params.sort = f.sort;
  if (f.query.trim()) params.query = f.query.trim();
  if (f.difficulty) params.difficulty = f.difficulty;
  if (f.tags.length > 0) params.tags = f.tags;
  if (f.offLeash) params.off_leash = 'true';
  if (f.wildlife) params.wildlife = 'true';
  if (f.minRating !== null) params.min_rating = String(f.minRating);
  if (f.minDistance !== null) params.min_distance = String(f.minDistance);
  if (f.maxDistance !== null) params.max_distance = String(f.maxDistance);
  if (f.minUserDistance !== null) params.min_user_distance = String(f.minUserDistance);
  if (f.maxUserDistance !== null) params.max_user_distance = String(f.maxUserDistance);
  if (f.minDuration !== null) params.min_duration = String(f.minDuration);
  if (f.maxDuration !== null) params.max_duration = String(f.maxDuration);
  if (f.lowDogTraffic) params.low_dog_traffic = 'true';
  if (f.lowFootTraffic) params.low_foot_traffic = 'true';
  if (f.lowExposure) params.low_exposure = 'true';

  return params;
}

export function useExploreFilters() {
  const [filters, setFilters] = useState<ExploreFilters>({ ...DEFAULT_FILTERS });

  const updateFilter = useCallback(<K extends keyof ExploreFilters>(key: K, value: ExploreFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleTag = useCallback((tag: string) => {
    setFilters((prev) => {
      const tags = prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag];
      return { ...prev, tags };
    });
  }, []);

  const clearAll = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
  }, []);

  const setLocation = useCallback((lat: number | null, lng: number | null, name: string | null) => {
    setFilters((prev) => ({
      ...prev,
      searchLatitude: lat,
      searchLongitude: lng,
      searchLocationName: name,
      mapCenterLatitude: null,
      mapCenterLongitude: null,
    }));
  }, []);

  const clearLocation = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      searchLatitude: null,
      searchLongitude: null,
      searchLocationName: null,
      mapCenterLatitude: null,
      mapCenterLongitude: null,
    }));
  }, []);

  const setMapCenter = useCallback((lat: number | null, lng: number | null) => {
    setFilters((prev) => ({
      ...prev,
      mapCenterLatitude: lat,
      mapCenterLongitude: lng,
    }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.sort !== DEFAULT_FILTERS.sort) count++;
    if (filters.query.trim()) count++;
    if (filters.tags.length > 0) count++;
    if (filters.difficulty !== null) count++;
    if (filters.minDistance !== null) count++;
    if (filters.maxDistance !== null) count++;
    if (filters.minUserDistance !== null) count++;
    if (filters.maxUserDistance !== null) count++;
    if (filters.minDuration !== null) count++;
    if (filters.maxDuration !== null) count++;
    if (filters.offLeash !== null) count++;
    if (filters.wildlife !== null) count++;
    if (filters.minRating !== null) count++;
    if (filters.lowDogTraffic !== null) count++;
    if (filters.lowFootTraffic !== null) count++;
    if (filters.lowExposure !== null) count++;
    if (filters.searchLatitude !== null || filters.searchLongitude !== null) count++;
    // mapCenter is not counted as an active filter
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  // Build query params for the /trail/search endpoint
  const buildQueryParams = useCallback((overrides?: Partial<ExploreFilters>): Record<string, string | string[]> => {
    return filtersToParams({ ...filters, ...overrides });
  }, [filters]);

  return {
    filters,
    setFilters,
    updateFilter,
    toggleTag,
    clearAll,
    setLocation,
    clearLocation,
    setMapCenter,
    activeFilterCount,
    hasActiveFilters,
    buildQueryParams,
  };
}